/**
 * Live Demo Iframe Mode — entry point for `?iraa-demo=<token>` links.
 *
 * Renders a split layout: the customer's site loads inside an iframe on one
 * side, and a chat panel runs alongside it. The agent (server-side) opens
 * with discovery questions tailored to the product, then transitions into
 * a guided showcase by issuing iframe commands (navigate, click, highlight,
 * etc.).
 *
 * Transport: HTTPS POST /api/v1/session/:id/chat per visitor turn. We avoid
 * WebSockets so customer-site CSPs that allow `https://*.iraa.ai` (but not
 * `wss://`) work out of the box — most CSPs treat the two schemes
 * separately, and asking every customer to add a wss directive is a tax on
 * adoption. The trade-off is we lose server-pushed typing indicators and
 * unsolicited messages; for guided demos that's fine — the agent only
 * speaks in response to the visitor.
 */
import { FullPageDemoMode, type DemoAccountInfo } from './full-page-demo.js';
import { drainSignals } from '../capture/signal-bus.js';
import { IRAA_LOGO_DATA_URI } from '../brand.js';

export interface LiveDemoStatus {
  audience?: 'lead' | 'joinee' | string;
  prefilledIdentity?: { name?: string | null; email?: string | null };
  suggestedFlowId?: string;
  singleUse?: boolean;
}

export interface LiveDemoIframeConfig {
  apiUrl: string;
  apiKey: string;
  productId?: string;
  demoToken: string;
  status?: LiveDemoStatus;
}

interface SessionCreateResponse {
  sessionId: string;
  token: string;
  greeting: { messageId?: string | null; speech?: string; text?: string; suggestions?: string[] } | null;
  agent?: { name?: string };
  branding?: { agentName?: string };
  demoAccount?: DemoAccountInfo;
  /**
   * Configured product URL (products.base_url). Used when the widget is
   * installed on a different origin than the product itself — e.g. landing
   * page on acme.com, app on app.acme.com. When undefined or same-origin,
   * the widget falls back to location.href.
   */
  productUrl?: string;
}

interface ChatResponse {
  messageId?: string | null;
  speech?: string;
  text?: string;
  iframeCommands?: Array<{ type: string; params: Record<string, unknown> }>;
  suggestions?: string[];
  agentId?: string;
  /**
   * Server hint for whether the widget should auto-tick a synthetic
   * continuation after this turn or yield to the visitor. See
   * apps/platform/src/routes/shared/session.ts for the producer side.
   */
  nextTurnHint?: 'auto_continue' | 'wait_for_visitor';
}

export class LiveDemoIframeMode {
  private config: LiveDemoIframeConfig;
  private fullPage: FullPageDemoMode | null = null;
  private host: HTMLDivElement | null = null;
  private shadow: ShadowRoot | null = null;
  private sessionId: string | null = null;
  private sessionToken: string | null = null;
  private sessionEnded = false;
  private pageHideHandler: (() => void) | null = null;

  constructor(config: LiveDemoIframeConfig) {
    this.config = config;
  }

  async start(): Promise<void> {
    // Show onboarding first so visitors know what to expect (and so we don't
    // burn an LLM-greeting session on people who immediately bounce).
    const proceed = await this.renderOnboarding();
    if (!proceed) return;

    this.renderBootingShell();
    let session: SessionCreateResponse;
    try {
      session = await this.createSession();
    } catch (err) {
      console.error('[DemoAgent] Failed to start iraa-demo session:', err);
      this.renderError("We couldn't start the demo. Please try again.");
      return;
    }

    this.sessionId = session.sessionId;
    this.sessionToken = session.token;

    this.pageHideHandler = () => this.endSession();
    window.addEventListener('pagehide', this.pageHideHandler);

    const productUrl = this.iframeProductUrl(session.productUrl);
    const agentName =
      session.branding?.agentName ?? session.agent?.name ?? 'Iraa AI';

    // Hide the booting shell so FullPageDemoMode can mount its own.
    this.removeBootingShell();

    const fullPage = new FullPageDemoMode({
      apiUrl: this.config.apiUrl,
      apiKey: this.config.apiKey,
      sessionId: session.sessionId,
      sessionToken: session.token,
      productUrl,
      sendVisitorMessage: (text, opts) => this.sendVisitorMessage(text, opts),
      sendLeadCapture: (data) => this.sendLeadCapture(data),
      existingMessages: session.greeting?.text
        ? [
            {
              id: 'greeting',
              role: 'agent',
              content: session.greeting.text,
              ts: Date.now(),
            },
          ]
        : [],
      agentName,
      initialGreetingSpeech: session.greeting?.speech ?? session.greeting?.text ?? undefined,
      initialGreetingMessageId: session.greeting?.messageId ?? undefined,
      demoAccount: session.demoAccount,
    });

    fullPage.onExit = () => this.stop();
    this.fullPage = fullPage;

    await fullPage.start();

    // Replay the greeting's iframe commands (none today, but kept for parity)
    // — handled by FullPageDemoMode's existingMessages path above.
  }

  stop(): void {
    this.endSession();
    if (this.pageHideHandler) {
      window.removeEventListener('pagehide', this.pageHideHandler);
      this.pageHideHandler = null;
    }
    this.fullPage?.stop();
    this.fullPage = null;
    this.removeBootingShell();
  }

  private endSession(): void {
    if (this.sessionEnded || !this.sessionId) return;
    this.sessionEnded = true;
    try {
      const url = `${this.config.apiUrl}/api/v1/session/${this.sessionId}/end`;
      const blob = new Blob(['{}'], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } catch {
      // sendBeacon failures are non-recoverable; the server-side sweep covers it.
    }
  }

  /**
   * URL to load inside the iframe.
   *
   * Cross-domain case: when the configured product URL lives on a different
   * origin than the page hosting the widget (landing on acme.com, product on
   * app.acme.com), use the configured URL verbatim. The visitor's current
   * path doesn't exist on the product domain, so preserving it would 404.
   *
   * Same-origin case: keep location.href (with iraa-demo stripped) so single-
   * domain installs preserve deep-link / UTM context. Stripping iraa-demo
   * also ensures the widget inside the iframe boots in iframe-agent mode
   * (via da_demo_iframe) rather than re-entering this live-demo loop.
   */
  private iframeProductUrl(configuredProductUrl?: string): string {
    if (configuredProductUrl) {
      try {
        if (new URL(configuredProductUrl).origin !== location.origin) {
          return configuredProductUrl;
        }
      } catch {
        // Malformed URL from the server — fall through to location.href so
        // the demo at least attempts to load rather than failing closed.
      }
    }
    const url = new URL(location.href);
    url.searchParams.delete('iraa-demo');
    return url.toString();
  }

  private async createSession(): Promise<SessionCreateResponse> {
    const resp = await fetch(`${this.config.apiUrl}/api/v1/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.config.apiKey}`,
      },
      body: JSON.stringify({
        productId: this.config.productId ?? 'default',
        demoToken: this.config.demoToken,
        visitorMeta: {
          referrer: document.referrer,
          url: location.href,
          language: navigator.language,
        },
      }),
    });

    if (!resp.ok) {
      throw new Error(`Session create failed: ${resp.status}`);
    }
    return (await resp.json()) as SessionCreateResponse;
  }

  private async sendVisitorMessage(
    text: string,
    opts?: { isAutoContinue?: boolean },
  ): Promise<void> {
    if (!this.sessionId || !this.sessionToken || !this.fullPage) return;

    const isAutoContinue = !!opts?.isAutoContinue;

    // Block until the widget's auto-login resolves. Without this gate, the
    // visitor's first message can land on the server while loginState is
    // still 'pending' — the agent then narrates "let me get us logged in"
    // while the iframe is mid-redirect to the dashboard. 3.5s covers the
    // typical ~6s autoLoginToDemoAccount budget without making fast logins
    // wait; if it times out we still send loginState='pending' and the
    // rewritten Rule A in prompt-builder will tell the agent to wait quietly.
    // Auto-continue ticks fire AFTER the first real visitor message has
    // already passed this gate, so the login state is already settled —
    // skipping the wait keeps continuation pacing tight.
    if (!isAutoContinue && this.fullPage.getLoginState() === 'pending') {
      await this.fullPage.waitForLoginSettled(3500);
    }

    // Drain the iframe's recent command results so the server-side agent can
    // see selector misses / failed clicks and recover instead of narrating
    // confidently over a broken step.
    const recentCommandResults = this.fullPage.drainRecentCommandResults();
    const currentUrl = this.fullPage.getCurrentIframeUrl() ?? undefined;
    const loginState = this.fullPage.getLoginState();
    // Voice state — 'off' / 'autoplay_blocked' tells the LLM to write longer,
    // more explanatory captions because the visitor is reading, not listening.
    const voiceState = this.fullPage.getVoiceState();
    // Drain customer signals (window.IraaDemo.signal) — events the customer's
    // site pushed since the last turn. The DOM is a lossy projection of app
    // state, so a "Stripe webhook returned" or "license issued" event from
    // the customer's JS lets the agent ground its narration in things the
    // verifier can't see.
    const customerSignals = drainSignals();

    // Try the streaming endpoint first — text appears letter-by-letter and
    // iframe commands fire as they're emitted (cinematic pacing). On any
    // failure (404 from older API, network hiccup, parse error) fall back to
    // the blocking endpoint so the visitor still gets a reply.
    try {
      const ok = await this.sendVisitorMessageStreaming(
        text, recentCommandResults, currentUrl, loginState, isAutoContinue, customerSignals, voiceState,
      );
      if (ok) return;
    } catch (err) {
      console.warn('[DemoAgent] Stream failed, falling back to blocking /chat:', err);
    }

    // Auto-continue ticks must not fall back to /chat — the blocking endpoint
    // does not yet accept the isAutoContinue flag, so it would treat the
    // empty message as a real visitor utterance (and 400 on the empty body).
    // Drop the tick silently; the visitor can resume by typing.
    if (isAutoContinue) {
      return;
    }

    try {
      const resp = await fetch(
        `${this.config.apiUrl}/api/v1/session/${this.sessionId}/chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.sessionToken}`,
          },
          body: JSON.stringify({
            message: text,
            recentCommandResults: recentCommandResults.length > 0 ? recentCommandResults : undefined,
            currentUrl,
            loginState,
            voiceState: voiceState !== 'on' ? voiceState : undefined,
            customerSignals: customerSignals.length > 0 ? customerSignals : undefined,
          }),
        },
      );

      if (!resp.ok) {
        throw new Error(`Chat failed: ${resp.status}`);
      }

      const data = (await resp.json()) as ChatResponse;
      this.fullPage.handleWSMessage({
        type: 'agent_response',
        payload: {
          messageId: data.messageId,
          text: data.text ?? '',
          speech: data.speech ?? data.text ?? '',
          iframeCommands: data.iframeCommands ?? [],
          suggestions: data.suggestions ?? [],
          agentId: data.agentId ?? 'demo',
          nextTurnHint: data.nextTurnHint,
        },
      });
    } catch (err) {
      console.error('[DemoAgent] Chat request failed:', err);
      this.fullPage.handleWSMessage({
        type: 'agent_response',
        payload: {
          text: "I'm having trouble reaching the server. Please try again in a moment.",
          speech: '',
          iframeCommands: [],
          suggestions: [],
          agentId: 'demo',
        },
      });
    }
  }

  /**
   * Streaming variant of sendVisitorMessage. Returns true on success (used
   * the streaming path end-to-end), false if it should fall back to the
   * blocking endpoint. Throws on hard failures.
   */
  private async sendVisitorMessageStreaming(
    text: string,
    recentCommandResults: Array<{ type: string; ok: boolean; error?: string; ts: number; recoveredSelector?: string }>,
    currentUrl: string | undefined,
    loginState: 'unknown' | 'pending' | 'logged_in' | 'failed',
    isAutoContinue: boolean,
    customerSignals: Array<{ ts: number; event: string; payload?: Record<string, unknown> }>,
    voiceState: 'on' | 'off' | 'autoplay_blocked',
  ): Promise<boolean> {
    if (!this.sessionId || !this.sessionToken || !this.fullPage) return false;

    const resp = await fetch(
      `${this.config.apiUrl}/api/v1/session/${this.sessionId}/chat/stream`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          Authorization: `Bearer ${this.sessionToken}`,
        },
        body: JSON.stringify({
          message: text,
          recentCommandResults: recentCommandResults.length > 0 ? recentCommandResults : undefined,
          currentUrl,
          loginState,
          voiceState: voiceState !== 'on' ? voiceState : undefined,
          isAutoContinue: isAutoContinue || undefined,
          customerSignals: customerSignals.length > 0 ? customerSignals : undefined,
        }),
      },
    );

    // 404/4xx → endpoint unavailable on this deployment; fall back.
    if (!resp.ok || !resp.body) {
      return false;
    }

    // Open an empty assistant bubble that we'll grow as text-deltas arrive.
    const messageHandle = this.fullPage.beginStreamingAssistantMessage();

    let fullText = '';
    let agentId = 'demo';
    let nextTurnHint: 'auto_continue' | 'wait_for_visitor' | undefined;
    let messageId: string | undefined;
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let sseBuffer = '';

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by a blank line. Process complete frames
        // and keep the incomplete tail in the buffer.
        let separatorIdx;
        while ((separatorIdx = sseBuffer.indexOf('\n\n')) !== -1) {
          const frame = sseBuffer.slice(0, separatorIdx);
          sseBuffer = sseBuffer.slice(separatorIdx + 2);
          const parsed = this.parseSseFrame(frame);
          if (!parsed) continue;
          const { event, data } = parsed;

          if (event === 'text-delta') {
            const t = (data?.text as string) ?? '';
            if (t) {
              fullText += t;
              this.fullPage.appendStreamingDelta(messageHandle, t);
            }
          } else if (event === 'iframe-cmd') {
            // Queue immediately — the cursor moves *while* the agent talks.
            this.fullPage.queueCommands([data as { type: string; params: Record<string, unknown> }]);
          } else if (event === 'done') {
            agentId = (data?.agentId as string) ?? 'demo';
            const hint = data?.nextTurnHint;
            if (hint === 'auto_continue' || hint === 'wait_for_visitor') {
              nextTurnHint = hint;
            }
            if (typeof data?.messageId === 'string') {
              messageId = data.messageId;
            }
          } else if (event === 'error') {
            // Mid-stream error — surface to the visitor.
            const m = (data?.message as string) ?? 'Stream error';
            this.fullPage.appendStreamingDelta(messageHandle, ` (${m})`);
          }
        }
      }
    } finally {
      this.fullPage.endStreamingAssistantMessage(messageHandle, fullText, agentId, nextTurnHint, messageId);
    }

    return fullText.length > 0;
  }

  /** Parse one SSE frame ("event: foo\ndata: {...}"). Returns null on garbage. */
  private parseSseFrame(frame: string): { event: string; data: Record<string, unknown> } | null {
    const lines = frame.split('\n');
    let event = 'message';
    let dataStr = '';
    for (const line of lines) {
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataStr += line.slice(5).trim();
      }
    }
    if (!dataStr) return { event, data: {} };
    try {
      return { event, data: JSON.parse(dataStr) };
    } catch {
      return null;
    }
  }

  private async sendLeadCapture(data: Record<string, string>): Promise<void> {
    if (!this.sessionId || !this.sessionToken) return;
    try {
      await fetch(
        `${this.config.apiUrl}/api/v1/session/${this.sessionId}/lead`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.sessionToken}`,
          },
          body: JSON.stringify(data),
        },
      );
    } catch (err) {
      console.warn('[DemoAgent] Lead capture failed:', err);
    }
  }

  /**
   * Front-door onboarding shown before session creation. Resolves true when
   * the visitor clicks Start, false if the host is dismissed (e.g. stop()).
   */
  private renderOnboarding(): Promise<boolean> {
    return new Promise((resolve) => {
      this.host = document.createElement('div');
      this.host.id = 'da-iraa-demo-onboarding';
      this.shadow = this.host.attachShadow({ mode: 'open' });
      this.shadow.innerHTML = `
        <style>
          :host { all: initial; }
          .wrap {
            position: fixed; inset: 0; z-index: 2147483646;
            display: flex; align-items: center; justify-content: center;
            background: radial-gradient(circle at 30% 20%, #1e1b4b 0%, #0f172a 60%, #020617 100%);
            color: #e2e8f0;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
            padding: 24px;
            box-sizing: border-box;
            animation: fadeIn 0.3s ease;
          }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          .card {
            max-width: 560px; width: 100%;
            background: rgba(15, 23, 42, 0.85);
            border: 1px solid rgba(99, 102, 241, 0.25);
            border-radius: 20px;
            padding: 40px 36px;
            box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6);
            backdrop-filter: blur(12px);
          }
          .badge {
            display: inline-flex; align-items: center; gap: 6px;
            padding: 4px 10px;
            font-size: 11px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase;
            color: #c7d2fe;
            background: rgba(99, 102, 241, 0.15);
            border: 1px solid rgba(99, 102, 241, 0.35);
            border-radius: 999px;
            margin-bottom: 18px;
          }
          .badge .dot {
            width: 6px; height: 6px; border-radius: 50%;
            background: #818cf8;
            box-shadow: 0 0 0 0 rgba(129, 140, 248, 0.7);
            animation: pulse 1.6s infinite;
          }
          @keyframes pulse {
            0% { box-shadow: 0 0 0 0 rgba(129, 140, 248, 0.7); }
            70% { box-shadow: 0 0 0 8px rgba(129, 140, 248, 0); }
            100% { box-shadow: 0 0 0 0 rgba(129, 140, 248, 0); }
          }
          h1 {
            font-size: 26px; font-weight: 700; line-height: 1.2;
            margin: 0 0 10px;
            color: #f8fafc;
          }
          .subtitle {
            font-size: 15px; line-height: 1.5;
            color: #94a3b8;
            margin: 0 0 28px;
          }
          .steps {
            display: grid; gap: 14px;
            margin-bottom: 32px;
          }
          .step {
            display: flex; gap: 14px; align-items: flex-start;
            padding: 14px 16px;
            background: rgba(30, 41, 59, 0.55);
            border: 1px solid rgba(148, 163, 184, 0.12);
            border-radius: 12px;
          }
          .step .num {
            flex-shrink: 0;
            width: 28px; height: 28px; border-radius: 8px;
            background: linear-gradient(135deg, #6366f1, #8b5cf6);
            color: white;
            display: flex; align-items: center; justify-content: center;
            font-size: 13px; font-weight: 700;
          }
          .step .body { font-size: 14px; line-height: 1.45; }
          .step .body b { color: #f1f5f9; font-weight: 600; }
          .step .body span { color: #94a3b8; }
          .cta {
            display: block; width: 100%;
            padding: 14px 20px;
            font-size: 15px; font-weight: 600;
            color: white;
            background: linear-gradient(135deg, #6366f1, #8b5cf6);
            border: none;
            border-radius: 12px;
            cursor: pointer;
            transition: transform 0.15s ease, box-shadow 0.15s ease;
            box-shadow: 0 8px 24px rgba(99, 102, 241, 0.35);
            font-family: inherit;
          }
          .cta:hover { transform: translateY(-1px); box-shadow: 0 12px 32px rgba(99, 102, 241, 0.5); }
          .cta:active { transform: translateY(0); }
          .footer {
            margin-top: 18px;
            font-size: 12px;
            color: #64748b;
            text-align: center;
          }
          .footer a { color: #94a3b8; text-decoration: none; }
          .footer a:hover { color: #c7d2fe; }
          .footer img { vertical-align: middle; margin-right: 6px; }
          .brand {
            display: flex; flex-direction: column; align-items: center;
            margin-bottom: 18px;
          }
          .brand-mark {
            width: 72px; height: 72px;
            object-fit: contain;
          }
          .brand-text {
            margin-top: 6px;
            font-size: 14px; font-weight: 700;
            color: #f1f5f9;
            letter-spacing: -0.01em;
          }
        </style>
        <div class="wrap" role="dialog" aria-modal="true" aria-labelledby="da-onboarding-title">
          <div class="card">
            <div class="brand">
              <img class="brand-mark" src="${IRAA_LOGO_DATA_URI}" alt="" aria-hidden="true" />
              <div class="brand-text">Iraa AI</div>
            </div>
            <div class="badge"><span class="dot"></span> Live AI Demo</div>
            <h1 id="da-onboarding-title">See it in action — guided by AI.</h1>
            <p class="subtitle">A live, personalized walkthrough. The product runs on one side; I'll chat with you and drive the demo on the other.</p>
            <div class="steps">
              <div class="step">
                <div class="num">1</div>
                <div class="body"><b>Quick discovery.</b> <span>I'll ask a couple of short questions to understand what's most relevant to you.</span></div>
              </div>
              <div class="step">
                <div class="num">2</div>
                <div class="body"><b>Guided tour.</b> <span>I'll click through the product and highlight the parts that match your needs.</span></div>
              </div>
              <div class="step">
                <div class="num">3</div>
                <div class="body"><b>Ask anything.</b> <span>Pricing, comparisons, edge cases — type whenever and I'll answer.</span></div>
              </div>
            </div>
            <button class="cta" id="da-onboarding-cta" type="button">Start demo →</button>
            <div class="footer"><img src="${IRAA_LOGO_DATA_URI}" alt="" width="14" height="14" />Powered by <a href="https://iraa.ai" target="_blank" rel="noopener">Iraa AI</a></div>
          </div>
        </div>
      `;
      document.body.appendChild(this.host);

      const cta = this.shadow.getElementById('da-onboarding-cta');
      const onClick = () => {
        cta?.removeEventListener('click', onClick);
        this.removeBootingShell();
        resolve(true);
      };
      cta?.addEventListener('click', onClick);
    });
  }

  private renderBootingShell(): void {
    this.host = document.createElement('div');
    this.host.id = 'da-iraa-demo-booting';
    this.shadow = this.host.attachShadow({ mode: 'open' });
    this.shadow.innerHTML = `
      <style>
        :host { all: initial; }
        .wrap {
          position: fixed; inset: 0; z-index: 2147483646;
          display: flex; align-items: center; justify-content: center;
          background: #0f172a; color: #e2e8f0;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }
        .card { text-align: center; }
        .boot-video {
          width: 96px; height: 96px;
          object-fit: contain;
          margin: 0 auto;
          display: block;
        }
        .boot-wordmark {
          font-size: 16px; font-weight: 700;
          color: #f1f5f9;
          letter-spacing: -0.01em;
          margin-top: 4px;
        }
        .label { font-size: 14px; color: #94a3b8; margin-top: 10px; }
      </style>
      <div class="wrap"><div class="card">
        <video class="boot-video" src="https://iraa.ai/iraa-animation.mp4" autoplay loop muted playsinline aria-hidden="true"></video>
        <div class="boot-wordmark">Iraa AI</div>
        <div class="label">Starting demo…</div>
      </div></div>
    `;
    document.body.appendChild(this.host);
  }

  private removeBootingShell(): void {
    this.host?.remove();
    this.host = null;
    this.shadow = null;
  }

  private renderError(message: string): void {
    if (!this.shadow) return;
    this.shadow.innerHTML = `
      <style>
        :host { all: initial; }
        .wrap {
          position: fixed; right: 24px; bottom: 24px; max-width: 320px;
          padding: 16px 18px; border-radius: 12px;
          background: #111; color: #fff;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          z-index: 2147483647;
        }
        .t { font-weight: 600; font-size: 14px; margin-bottom: 4px; }
        .b { font-size: 13px; opacity: 0.8; line-height: 1.4; }
      </style>
      <div class="wrap">
        <div class="t">Demo unavailable</div>
        <div class="b">${message}</div>
      </div>
    `;
  }
}
